export {
  FALLBACK_FIBRE_COMPONENT_TYPE_OPTIONS as COMPONENT_TYPE_OPTIONS,
  FALLBACK_FIBRE_TYPE_OPTIONS as FIBRE_TYPE_OPTIONS,
} from "./gpsrSelectOptions";

let fibreIdCounter = 0;

export const createFibreId = (prefix = "gpsr-fibre") => {
  fibreIdCounter += 1;
  return `${prefix}-${Date.now()}-${fibreIdCounter}`;
};

/** Client-only textile component ids from createFibreId("gpsr-textile-component"). */
export const isGpsrClientGeneratedComponentId = (id) => {
  const raw = String(id ?? "");
  return raw.startsWith("gpsr-textile-component-");
};

/** True when id is present and not a local drawer/temp component id. */
export const isGpsrServerFibreComponentId = (id) => {
  if (id == null || id === "") return false;
  return !isGpsrClientGeneratedComponentId(id);
};

export const buildEmptyFibreRow = () => ({
  id: createFibreId(),
  fibreType: "",
  percentage: "",
});

export const buildEmptyComponentDraft = () => ({
  id: createFibreId("gpsr-textile-component"),
  componentType: "",
  fibres: [buildEmptyFibreRow()],
});

export const buildInitialFibreComposition = () => ({
  components: [],
});

export const cloneComponentDraft = (component) => {
  if (!component || typeof component !== "object") {
    return buildEmptyComponentDraft();
  }
  const fibres = Array.isArray(component.fibres) ? component.fibres : [];
  return {
    id: component.id || createFibreId("gpsr-textile-component"),
    componentType: String(component.componentType ?? ""),
    fibres:
      fibres.length > 0
        ? fibres.map((fibre) => ({
            id: fibre?.id || createFibreId(),
            fibreType: String(fibre?.fibreType ?? ""),
            percentage:
              fibre?.percentage == null || fibre?.percentage === ""
                ? ""
                : String(fibre.percentage),
          }))
        : [buildEmptyFibreRow()],
  };
};

export const parsePercentage = (value) => {
  const raw = String(value ?? "").trim().replace(",", ".");
  if (!raw) return null;
  const num = Number(raw);
  if (!Number.isFinite(num)) return null;
  return num;
};

export const roundPercentage = (value) => Math.round(value * 100) / 100;

export const calcFibresTotal = (fibres = []) => {
  const total = (Array.isArray(fibres) ? fibres : []).reduce((sum, fibre) => {
    const parsed = parsePercentage(fibre?.percentage);
    return sum + (parsed == null ? 0 : parsed);
  }, 0);
  return roundPercentage(total);
};

export const formatFibreSummary = (fibres = [], resolveFibreTypeLabel) => {
  const parts = (Array.isArray(fibres) ? fibres : [])
    .map((fibre) => {
      const type = String(fibre?.fibreType ?? "").trim();
      const pct = parsePercentage(fibre?.percentage);
      if (!type || pct == null) return null;
      const typeLabel =
        typeof resolveFibreTypeLabel === "function"
          ? resolveFibreTypeLabel(type) || type
          : type;
      const display =
        Number.isInteger(pct) || Math.abs(pct - Math.round(pct)) < 0.001
          ? String(Math.round(pct))
          : String(roundPercentage(pct));
      return `${typeLabel} ${display}%`;
    })
    .filter(Boolean);
  return parts.length > 0 ? parts.join(" • ") : "—";
};

export const formatTotalIndicator = (total) => {
  const rounded = roundPercentage(Number(total) || 0);
  if (rounded === 100) return "Total: 100%";
  if (rounded < 100) {
    const remaining = roundPercentage(100 - rounded);
    return `Total: ${rounded}% — ${remaining}% remaining`;
  }
  const excess = roundPercentage(rounded - 100);
  return `Total: ${rounded}% — exceeds by ${excess}%`;
};

export const validateTextileComponentDraft = (draft) => {
  const errors = {
    componentType: "",
    fibres: {},
    total: "",
  };

  const componentType = String(draft?.componentType ?? "").trim();
  if (!componentType) {
    errors.componentType = "Component type is required";
  }

  const fibres = Array.isArray(draft?.fibres) ? draft.fibres : [];
  const seenTypes = new Map();
  let completeRows = 0;

  fibres.forEach((fibre) => {
    const fibreId = fibre?.id;
    const fibreType = String(fibre?.fibreType ?? "").trim();
    const percentageRaw = String(fibre?.percentage ?? "").trim();
    const percentage = parsePercentage(percentageRaw);
    const rowErrors = {};

    if (!fibreType) {
      rowErrors.fibreType = "Fibre type is required";
    } else if (seenTypes.has(fibreType)) {
      rowErrors.fibreType = "Fibre type already selected";
    } else {
      seenTypes.set(fibreType, fibreId);
    }

    if (!percentageRaw) {
      rowErrors.percentage = "Percentage is required";
    } else if (percentage == null) {
      rowErrors.percentage = "Enter a valid number";
    } else if (percentage <= 0) {
      rowErrors.percentage = "Must be greater than 0";
    } else if (percentage > 100) {
      rowErrors.percentage = "Must be at most 100";
    }

    if (Object.keys(rowErrors).length === 0) {
      completeRows += 1;
    } else if (fibreId) {
      errors.fibres[fibreId] = rowErrors;
    }
  });

  if (completeRows < 1) {
    errors.total = "Add at least one complete fibre row";
  }

  const total = calcFibresTotal(fibres);
  if (completeRows >= 1 && total !== 100) {
    errors.total = "Fibre percentages must total exactly 100%";
  }

  const isValid =
    !errors.componentType &&
    !errors.total &&
    Object.keys(errors.fibres).length === 0;

  return { isValid, errors, total };
};
